import React, { useState, useEffect } from 'react';
import { datasetService, Dataset } from '../services/datasetService';
import '../styles/dashboard-pages.css';

const QuickTrainPage: React.FC = () => {
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    setLoading(true);
    setError(null);
    try {
      const datasetList = await datasetService.listDatasets();
      console.log('[QuickTrainPage] Loaded datasets:', datasetList);
      setDatasets(datasetList);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load datasets';
      setError(errorMsg);
      console.error('[QuickTrainPage] Error loading datasets:', err);
      alert(`Error loading datasets: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Quick Train</h1>
        <p>Train a model using a pre-configured dataset</p>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <h2>Select Dataset</h2>
          <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
            Choose from your configured datasets that are ready to use
          </p>
          
          {loading ? (
            <div className="empty-state">
              <div className="empty-icon">⏳</div>
              <p>Loading datasets...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <p>Error: {error}</p>
              <button className="action-btn secondary" onClick={loadDatasets} style={{ marginTop: '12px' }}>
                Retry
              </button>
            </div>
          ) : datasets.length > 0 ? (
            <div className="datasets-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
              {datasets.map((dataset) => (
                <div
                  key={dataset.id}
                  className={`dataset-select-card ${selectedDataset === dataset.id ? 'selected' : ''}`}
                  onClick={() => setSelectedDataset(dataset.id)}
                  style={{
                    padding: '20px',
                    background: selectedDataset === dataset.id 
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(31, 41, 55, 0.5) 0%, rgba(30, 41, 59, 0.5) 100%)',
                    border: `1px solid ${selectedDataset === dataset.id ? 'rgba(99, 102, 241, 0.5)' : 'rgba(148, 163, 184, 0.1)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#e4e4e7', marginBottom: '8px' }}>
                    {dataset.display_name || dataset.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                    Type: {dataset.dataset_type || 'N/A'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    Samples: {dataset.total_samples ? dataset.total_samples.toLocaleString() : 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <p>No datasets yet. Create your first dataset to get started.</p>
            </div>
          )}
        </section>

        {selectedDataset && (
          <section className="section-card">
            <h2>Training Configuration</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group">
                <label>Model Name</label>
                <input type="text" placeholder="Enter model name" />
              </div>
              <div className="form-group">
                <label>Epochs</label>
                <input type="number" placeholder="10" defaultValue="10" />
              </div>
              <div className="form-group">
                <label>Batch Size</label>
                <input type="number" placeholder="32" defaultValue="32" />
              </div>
              <div className="form-group">
                <label>Learning Rate</label>
                <input type="number" step="0.001" placeholder="0.001" defaultValue="0.001" />
              </div>
              <button className="action-btn primary" style={{ alignSelf: 'flex-start' }}>
                Start Training
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default QuickTrainPage;

